import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import useProfile from '../../hooks/useProfile';
import useChat from '../../hooks/useChat';
import useDropdownKeyboard from '../../hooks/useDropdownKeyboard';
import { getAvatarUrl } from '@ego/api';
import CreateChannelModal from '../CreateChannelModal/CreateChannelModal';
import EditProfileModal from '../EditProfileModal/EditProfileModal';
import SearchOverlay from '../SearchOverlay/SearchOverlay';
import styles from './Navbar.module.css';

export default function Navbar() {
  const { session, signOut } = useAuth();
  const { profile, refetch: refetchProfile } = useProfile();
  const { toggleChat } = useChat();
  const { pathname } = useLocation();
  const isLoginPage = pathname === '/login' || pathname === '/signup';
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const closeDropdown = useCallback(() => setShowDropdown(false), []);
  useDropdownKeyboard(menuRef, showDropdown, closeDropdown);

  // Global Cmd/Ctrl+K toggles the block-search overlay (owner-only feature).
  useEffect(() => {
    if (!session || !profile?.handle) return;
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      if (e.key.toLowerCase() !== 'k') return;
      if (!showSearch) {
        // Don't hijack the shortcut away from a focused editing context.
        const target = e.target;
        const inEditable = target instanceof HTMLElement &&
          (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
        if (inEditable) return;
      }
      e.preventDefault();
      setShowSearch(prev => !prev);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [session, profile?.handle, showSearch]);

  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  const chatLabel = session ? 'Talk to yourself' : 'Chat with EGO';

  const avatarUrl = getAvatarUrl(profile?.avatarPath);

  return (
    <>
      <header className={styles.navbar}>
        <div className={styles.left}>
          <a href="/explore" className={styles.logoLink} aria-label="EGO home">
            <svg className={styles.logo} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 0.5L10.2 5.8L9 4.2L7.8 5.8L9 0.5Z" fill="currentColor"/>
              <path d="M9 17.5L10.2 12.2L9 13.8L7.8 12.2L9 17.5Z" fill="currentColor"/>
              <path d="M0.5 9L5.8 7.8L4.2 9L5.8 10.2L0.5 9Z" fill="currentColor"/>
              <path d="M17.5 9L12.2 10.2L13.8 9L12.2 7.8L17.5 9Z" fill="currentColor"/>
              <path d="M2.2 2.2L7 5.5L5.5 5.5L5.5 7L2.2 2.2Z" fill="currentColor"/>
              <path d="M15.8 2.2L13 7L13 5.5L11 5.5L15.8 2.2Z" fill="currentColor"/>
              <path d="M2.2 15.8L5.5 11L5.5 13L7 13L2.2 15.8Z" fill="currentColor"/>
              <path d="M15.8 15.8L11 13L13 13L13 11L15.8 15.8Z" fill="currentColor"/>
            </svg>
          </a>
          {!isLoginPage && <button className={styles.searchText} onClick={toggleChat}>{chatLabel}</button>}
        </div>
        <div className={styles.right}>
          {session ? (
            <>
              {profile?.handle && (
                <button
                  className={styles.searchBtn}
                  onClick={() => setShowSearch(true)}
                  aria-label="Search blocks"
                  title="Search blocks (⌘K)"
                >
                  <Search size={16} />
                </button>
              )}
              {profile?.handle && (
                <button className={styles.newChannel} onClick={() => setShowCreateModal(true)}>
                  New channel
                  <span className={styles.plus}>&nbsp;+</span>
                </button>
              )}
              <div className={styles.avatarWrapper} ref={dropdownRef}>
                <button
                  className={styles.avatar}
                  onClick={() => setShowDropdown(prev => !prev)}
                  aria-haspopup="true"
                  aria-expanded={showDropdown}
                  aria-label="User menu"
                >
                  {avatarUrl && (
                    <img src={avatarUrl} alt="" className={styles.avatarImg} />
                  )}
                </button>
                {showDropdown && (
                  <div className={styles.dropdown} role="menu" aria-label="User menu" ref={menuRef}>
                    {profile?.handle && (
                      <Link
                        to={`/${profile.handle}`}
                        className={styles.dropdownItem}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => setShowDropdown(false)}
                      >
                        My profile
                      </Link>
                    )}
                    {profile?.handle && (
                      <button
                        className={styles.dropdownItem}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => { setShowEditProfile(true); setShowDropdown(false); }}
                      >
                        Edit profile
                      </button>
                    )}
                    <button
                      className={styles.dropdownItem}
                      role="menuitem"
                      tabIndex={-1}
                      onClick={() => { signOut(); setShowDropdown(false); }}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link to="/signup" className={styles.loginLink}>Sign in</Link>
          )}
        </div>
      </header>
      {showCreateModal && (
        <CreateChannelModal onClose={() => setShowCreateModal(false)} ownerHandle={profile?.handle} />
      )}
      {showEditProfile && profile && (
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEditProfile(false)}
          onSaved={refetchProfile}
        />
      )}
      {showSearch && profile?.handle && (
        <SearchOverlay ownerHandle={profile.handle} onClose={() => setShowSearch(false)} />
      )}
    </>
  );
}
